import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Globe, KeyRound } from "lucide-react";

export default function AdminSettings() {
  const [siteTitle, setSiteTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSiteTitle(data.site_title || "")).catch(() => {});
  }, []);

  const handleTitleSave = async (e) => {
    e.preventDefault();
    if (!siteTitle.trim()) { toast.error("Site title cannot be empty"); return; }
    setSavingTitle(true);
    try {
      const { data } = await api.patch("/admin/settings", { site_title: siteTitle.trim() });
      document.title = data.site_title;
      toast.success("Site title updated");
    } catch {
      toast.error("Failed to update site title");
    } finally {
      setSavingTitle(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords do not match"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password changed successfully");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <Shell role="admin">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Settings</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Site Settings</h1>
      </div>

      <div className="max-w-xl space-y-8">

        {/* Site Title */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center">
              <Globe className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">Site Title</div>
              <div className="text-xs text-slate-500">Shown in the browser tab</div>
            </div>
          </div>
          <form onSubmit={handleTitleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Title</Label>
              <Input
                value={siteTitle}
                onChange={(e) => setSiteTitle(e.target.value)}
                placeholder="Blue Cross Life Sciences"
                className="h-11"
              />
            </div>
            <Button type="submit" disabled={savingTitle} className="bg-brand-600 hover:bg-brand-700">
              {savingTitle ? "Saving…" : "Save Title"}
            </Button>
          </form>
        </Card>

        {/* Change Password */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center">
              <KeyRound className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">Change Password</div>
              <div className="text-xs text-slate-500">Update your admin account password</div>
            </div>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Current Password</Label>
              <Input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                placeholder="Enter current password"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">New Password</Label>
              <Input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Min. 6 characters"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Repeat new password"
                required
                className="h-11"
              />
            </div>
            <Button type="submit" disabled={savingPwd} className="bg-brand-600 hover:bg-brand-700">
              {savingPwd ? "Changing…" : "Change Password"}
            </Button>
          </form>
        </Card>

      </div>
    </Shell>
  );
}
